import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { getSupportedLanguages, isLanguageSupported } from '@/chassis/ui/i18n/i18n-helper';

export type LanguageContextValue = {
    language: Language;
    setLanguage: (lang: Language) => void;
    supportedLanguages: Language[];
};

export const LanguageContext = createContext<LanguageContextValue | null>(null);

const STORAGE_KEY = 'cdcb:ui:language';

function resolveInitialLanguage(): Language {
    if (typeof window !== 'undefined') {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored && isLanguageSupported(stored)) {
            return stored;
        }

        const navigatorLang = (window.navigator?.language || window.navigator?.languages?.[0] || '').slice(0, 2).toLowerCase();
        if (navigatorLang && isLanguageSupported(navigatorLang)) {
            return navigatorLang;
        }
    }

    return 'pt';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
    const [language, setLanguageState] = useState<Language>(() => resolveInitialLanguage());
    const supportedLanguages = useMemo(() => getSupportedLanguages(), []);

    const setLanguage = useCallback((lang: Language) => {
        setLanguageState((current) => (current === lang ? current : lang));
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, language);
        }
        if (typeof document !== 'undefined') {
            document.documentElement.lang = language;
        }
    }, [language]);

    const value = useMemo<LanguageContextValue>(() => ({
        language,
        setLanguage,
        supportedLanguages,
    }), [language, setLanguage, supportedLanguages]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

const noop: LanguageContextValue['setLanguage'] = () => undefined;

export function useLanguageContext(): LanguageContextValue {
    const ctx = useContext(LanguageContext);
    if (ctx) {
        return ctx;
    }
    return {
        language: 'pt',
        setLanguage: noop,
        supportedLanguages: getSupportedLanguages(),
    };
}
